import {
  RepeatableShiftOptions,
  RepeatableShiftsService,
  ShiftOptions,
  ShiftsService,
} from '@api'
import { isAxiosError } from 'axios'
import { useMutation, useQueryClient } from 'react-query'

import { toast } from '@/models/toasts'
import { handleError } from '@/utils/error'

import type {
  DeleteShiftRequestOptions,
  EditRepeatableShiftMutationOptions,
  EditSingleShiftMutationOptions,
  ShiftGroupsMapByCreators,
} from '../types'

export const useShiftMutations = () => {
  const queryClient = useQueryClient()

  const invalidateShifts = () => {
    queryClient.invalidateQueries('shifts')
  }

  const handleMutateShiftError = (error: unknown) => {
    if (isAxiosError(error) && error.response?.status === 409) {
      handleError('You already created a shift at this time')
    } else {
      handleError(
        error,
        'Something went wrong. Please contact the support for more information.',
      )
    }
  }

  const createSingleShiftMutation = useMutation(
    (data: ShiftOptions) => ShiftsService.createShift({ requestBody: data }),
    {
      onSuccess: (createdShift) => {
        toast.success('Shift was successfully created')
        queryClient.setQueriesData<ShiftGroupsMapByCreators | undefined>(
          'shifts',
          (shiftsMap = {}) => {
            const shifts = shiftsMap[createdShift.creatorId] ?? []

            return {
              ...shiftsMap,
              [createdShift.creatorId]: [...shifts, createdShift],
            }
          },
        )
      },
      onError: handleMutateShiftError,
    },
  )

  const createRepeatableShiftMutation = useMutation(
    (data: RepeatableShiftOptions) =>
      RepeatableShiftsService.createRepeatableShift({ requestBody: data }),
    {
      onSuccess: () => {
        toast.success('Shifts were successfully created')
        invalidateShifts()
      },
      onError: handleMutateShiftError,
    },
  )

  const deleteShiftMutation = useMutation(
    ({ shift, target }: DeleteShiftRequestOptions) => {
      if (!shift.repeatableShiftId) {
        return ShiftsService.deleteShift({ shiftId: shift.id })
      }
      if (!target) {
        throw new Error(
          'Target must be specified for repeatable shifts deletion',
        )
      }

      return RepeatableShiftsService.deleteRepeatableShift({
        target,
        repeatableShiftId: shift.repeatableShiftId,
        requestBody: {
          anchorShiftId: shift.id,
          anchorShiftStartedAt: shift.startedAt,
        },
      })
    },
    {
      onSuccess: (_, { shift, target }) => {
        const message =
          target === 'All'
            ? 'Shifts were successfully deleted'
            : 'Shift was successfully deleted'

        toast.success(message)
        queryClient.setQueriesData<ShiftGroupsMapByCreators | undefined>(
          'shifts',
          (shiftsMap) => {
            if (!shiftsMap) return shiftsMap

            const shifts = shiftsMap[shift.creatorId] ?? []

            let newShifts = shifts

            if (!shift.repeatableShiftId) {
              newShifts = shifts.filter(({ id }) => id !== shift.id)
            } else {
              newShifts = shifts.filter(({ startedAt, repeatableShiftId }) => {
                if (repeatableShiftId !== shift.repeatableShiftId) return true

                if (target === 'Shift') {
                  return startedAt !== shift.startedAt
                }

                return (
                  new Date(startedAt).getTime() <
                  new Date(shift.startedAt).getTime()
                )
              })
            }

            return {
              ...shiftsMap,
              [shift.creatorId]: newShifts,
            }
          },
        )
      },
      onError: (error, { target }) => {
        const message =
          target === 'All'
            ? 'Failed to delete shifts'
            : 'Failed to delete shift'

        handleError(error, message)
      },
    },
  )

  const editRepeatableShiftMutation = useMutation(
    ({ data, originalShift, target }: EditRepeatableShiftMutationOptions) =>
      RepeatableShiftsService.updateRepeatableShift({
        requestBody: {
          options: data,
          anchorShiftId: originalShift.id,
          anchorShiftStartedAt: originalShift.startedAt,
        },
        repeatableShiftId: originalShift.repeatableShiftId!,
        target,
      }),
    {
      onSuccess: () => {
        toast.success('Shift was successfully edited')
        invalidateShifts()
      },
      onError: handleMutateShiftError,
    },
  )
  const editSingleShiftMutation = useMutation(
    ({ data, originalShift }: EditSingleShiftMutationOptions) =>
      ShiftsService.updateShift({
        shiftId: originalShift.id,
        requestBody: data,
      }),
    {
      onSuccess: (updatedShift) => {
        toast.success('Shift was successfully edited')
        queryClient.setQueriesData<ShiftGroupsMapByCreators | undefined>(
          'shifts',
          (shiftsMap) => {
            if (!shiftsMap) return shiftsMap

            const shifts = shiftsMap[updatedShift.creatorId]

            return {
              ...shiftsMap,
              [updatedShift.creatorId]: shifts.map((shift) =>
                shift.id === updatedShift.id ? updatedShift : shift,
              ),
            }
          },
        )
      },
      onError: handleMutateShiftError,
    },
  )

  return {
    createSingleShift: createSingleShiftMutation.mutateAsync,
    createRepeatableShift: createRepeatableShiftMutation.mutateAsync,
    isCreating:
      createSingleShiftMutation.isLoading ||
      createRepeatableShiftMutation.isLoading,
    deleteShift: deleteShiftMutation.mutateAsync,
    editRepeatableShift: editRepeatableShiftMutation.mutateAsync,
    editSingleShift: editSingleShiftMutation.mutateAsync,
    isEditing:
      editRepeatableShiftMutation.isLoading ||
      editSingleShiftMutation.isLoading,
  }
}
